import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Box, Typography, TextField, IconButton,
} from '@mui/material';
import {
    Close as CloseIcon,
    ErrorOutline as ErrorOutlineIcon,
    ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';

/**
 * Модалка коррекции отклонённой записи часов.
 *
 * Появляется вместо обычной клавиатуры когда worker тапает по ячейке
 * со status='rejected'. Показывает:
 *   - причину отклонения (крупно, красная плашка)
 *   - кто отклонил и когда
 *   - старое значение часов (зачёркнуто)
 *   - поле для ввода нового значения
 *   - кнопку Confirm correction
 *
 * После save часы обновляются, rejection auto-clear'ится на сервере,
 * запись уходит в status='draft' — foreman должен подписать заново.
 */
export default function RejectionCorrectionModal({
    open,
    employeeName,
    jobName,
    jobCode,
    workDate,
    originalHours,
    rejectionReason,
    rejectedByName,
    rejectedAt,
    onCancel,
    onConfirm,
    onKeep,
}) {
    const [newHours, setNewHours] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (open) {
            setNewHours(originalHours > 0 ? String(originalHours) : '');
            setSubmitting(false);
        }
    }, [open, originalHours]);

    const parsedHours = parseFloat(newHours);
    const isValidHours = newHours !== '' && !isNaN(parsedHours) && parsedHours >= 0 && parsedHours <= 24;
    const isChanged = isValidHours && Math.abs(parsedHours - originalHours) > 0.001;

    // Спор от работника приходит с пометкой «[worker] » — показываем, от кого он, без пометки.
    const fromWorker = /^\[worker\]\s*/i.test(rejectionReason || '');
    const reasonText = (rejectionReason || '').replace(/^\[worker\]\s*/i, '');

    // Часы верные — оставить как есть и снять спор, ничего не перебивая.
    const handleKeep = async () => {
        if (submitting || !onKeep) return;
        setSubmitting(true);
        try {
            await onKeep();
        } finally {
            setSubmitting(false);
        }
    };

    const handleConfirm = async () => {
        if (!isValidHours || submitting) return;
        setSubmitting(true);
        try {
            await onConfirm(parsedHours);
        } finally {
            setSubmitting(false);
        }
    };

    const formatRejectedAt = (iso) => {
        if (!iso) return '';
        try {
            const d = new Date(iso);
            return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
        } catch { return iso; }
    };

    return (
        <Dialog open={open} onClose={submitting ? undefined : onCancel} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ bgcolor: '#fef2f2', borderBottom: '1px solid #fecaca', display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
                <ErrorOutlineIcon sx={{ color: '#dc2626' }} />
                <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" fontWeight={700} color="#991b1b">
                        Entry rejected — correction needed
                    </Typography>
                    <Typography variant="caption" color="#7f1d1d" sx={{ display: 'block' }}>
                        {employeeName} · {jobCode ? `${jobCode} ` : ''}{jobName}
                    </Typography>
                </Box>
                <IconButton onClick={onCancel} disabled={submitting} sx={{ position: 'absolute', right: 8, top: 8, color: '#991b1b' }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: 2 }}>
                {/* Rejection reason — visible and prominent */}
                <Box sx={{
                    bgcolor: '#fef2f2',
                    border: '1px solid #fecaca',
                    borderLeft: '4px solid #dc2626',
                    borderRadius: 1,
                    p: 1.5,
                    mb: 2,
                }}>
                    <Typography variant="caption" color="#7f1d1d" sx={{ display: 'block', fontWeight: 700, mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {fromWorker ? 'Reason from worker' : 'Reason from admin'}
                    </Typography>
                    <Typography variant="body2" color="#991b1b" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                        {reasonText || '(no reason given)'}
                    </Typography>
                    {(rejectedByName || rejectedAt) && (
                        <Typography variant="caption" color="#7f1d1d" sx={{ display: 'block', mt: 1, opacity: 0.8 }}>
                            — {rejectedByName || 'admin'}{rejectedAt ? `, ${formatRejectedAt(rejectedAt)}` : ''}
                        </Typography>
                    )}
                </Box>

                {/* Change from old → new */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
                            Current
                        </Typography>
                        <Typography variant="h5" sx={{ textDecoration: 'line-through', color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                            {originalHours}h
                        </Typography>
                    </Box>
                    <ArrowForwardIcon sx={{ color: 'text.disabled' }} />
                    <Box sx={{ flex: 1, minWidth: 120 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
                            New hours
                        </Typography>
                        <TextField
                            autoFocus
                            fullWidth
                            size="small"
                            type="number"
                            value={newHours}
                            onChange={(e) => setNewHours(e.target.value)}
                            inputProps={{ min: 0, max: 24, step: 0.5, inputMode: 'decimal', style: { fontSize: '1.5rem', fontWeight: 600, textAlign: 'center' } }}
                            error={newHours !== '' && !isValidHours}
                            helperText={newHours !== '' && !isValidHours ? '0–24' : ' '}
                            disabled={submitting}
                        />
                    </Box>
                </Box>

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4 }}>
                    Hours are right? <b>Keep {originalHours}h</b> clears the dispute and leaves the hours as they are.
                    Changing the hours clears it too and resets the week's signature (signing again is optional).
                </Typography>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
                <Button onClick={onCancel} disabled={submitting}>Cancel</Button>
                {onKeep && (
                    <Button variant="outlined" onClick={handleKeep} disabled={submitting}>
                        Keep {originalHours}h
                    </Button>
                )}
                <Button
                    variant="contained"
                    color="error"
                    onClick={handleConfirm}
                    disabled={!isValidHours || !isChanged || submitting}
                >
                    {submitting ? 'Saving…' : 'Confirm correction'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
